import React from "react";
import { Box, Card, CardContent, Typography, Chip, alpha } from "@mui/material";
import { STATUS_CONFIG, WEEK_DAYS, GRID_TYPE_META } from "../constants";
import { getCalendarDays, isToday, isInternalCalendarEvent, isGhostOccurrence, eventBelongsToCalendarDay } from "../utils";

const uniqueDayItems = (dayEvents) => {
  const items = [];
  const seen = new Set();
  dayEvents.forEach((ev) => {
    const id = String(ev.originalServiceId || ev._id);
    if (seen.has(id)) return;
    seen.add(id);
    items.push(ev);
  });
  return items;
};

const getItemTypeMeta = (item) => {
  const isEvent = isInternalCalendarEvent(item);
  const raw = String((isEvent ? item.eventType : item.type) || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  const key = raw || (isEvent ? "EVENTO" : "OTRO");
  return (
    GRID_TYPE_META[key] || {
      label: isEvent ? "Evento" : item.type || "Otro",
      color: "#57534E",
    }
  );
};

const getBranchLabel = (item) => {
  if (isInternalCalendarEvent(item)) {
    return String(item.title || item.eventType || "Evento").trim() || "Evento";
  }
  const branch = item?.branch;
  const name =
    branch?.name || (typeof branch === "string" ? branch : "") || "";
  return String(name).trim() || "Sin sucursal";
};

const getTypeCounts = (items) => {
  const map = new Map();
  items.forEach((item) => {
    const meta = getItemTypeMeta(item);
    const isEvent = isInternalCalendarEvent(item);
    const raw = String((isEvent ? item.eventType : item.type) || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");
    const key = raw || (isEvent ? "EVENTO" : "OTRO");
    const current = map.get(key);
    if (current) current.count += 1;
    else map.set(key, { key, label: meta.label, color: meta.color, count: 1 });
  });
  return [...map.values()].sort((a, b) => b.count - a.count);
};

const getBranchCounts = (items) => {
  const map = new Map();
  items.forEach((item) => {
    const label = getBranchLabel(item);
    const key = label.toLocaleLowerCase("es");
    const current = map.get(key);
    if (current) current.count += 1;
    else {
      map.set(key, {
        key,
        label,
        color: getItemTypeMeta(item).color,
        count: 1,
      });
    }
  });
  return [...map.values()].sort((a, b) =>
    a.label.localeCompare(b.label, "es", { sensitivity: "base" })
  );
};

export const CalendarGrid = ({
  currentDate,
  events,
  selectedDay,
  onDaySelect,
  compact = false,
  ghostTheme = false,
  showBranches = false,
}) => {
  const calendarDays = (() => {
    const days = [...getCalendarDays(currentDate)];
    while (days.length > 7 && days.slice(-7).every((d) => !d)) {
      days.splice(-7);
    }
    return days;
  })();
  const weekCount = Math.max(1, Math.ceil(calendarDays.length / 7));

  const getDayEvents = (day) => {
    return events.filter(
      (e) => eventBelongsToCalendarDay(e, day)
    );
  };

  const getDayColor = (dayEvents, ghostOnly) => {
    if (dayEvents.length === 0) return ghostTheme ? "#cbd5e1" : "#e2e8f0";
    if (ghostOnly) return "#64748b";
    const firstEvent = dayEvents[0];
    const status = firstEvent.status || "SCHEDULED";
    return STATUS_CONFIG[status]?.color || "#1976d2";
  };

  const getDayBgColor = (day, dayColor) => {
    if (selectedDay === day) return alpha(dayColor === "#e2e8f0" ? "#2563eb" : dayColor, 0.14);
    if (isToday(day, currentDate)) return alpha("#2563eb", 0.06);
    return "#fff";
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        gap={0.5}
        mb={compact ? 0.5 : 0.75}
        sx={{ justifyContent: "flex-end", flexWrap: "wrap", flexShrink: 0 }}
      >
        {compact
          ? null
          : Object.entries(STATUS_CONFIG).map(([key, { color, label }]) => (
              <Chip
                key={key}
                label={label}
                sx={{
                  bgcolor: color,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.58rem",
                  height: 20,
                  "& .MuiChip-label": { px: 0.7 },
                }}
                size="small"
              />
            ))}
        {ghostTheme || (events || []).some(isGhostOccurrence) ? (
          <Chip
            label="Fantasma"
            sx={{
              bgcolor: "transparent",
              color: "#64748b",
              fontWeight: 700,
              fontSize: "0.58rem",
              height: 20,
              border: "1px dashed #94a3b8",
              "& .MuiChip-label": { px: 0.7 },
            }}
            size="small"
          />
        ) : null}
      </Box>

      {/* Weekdays Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          mb: 0.75,
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        {WEEK_DAYS.map((day) => (
          <Typography
            key={day}
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              py: 0.7,
              fontSize: "0.75rem",
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gridTemplateRows: `repeat(${weekCount}, minmax(0, 1fr))`,
          gap: 0.85,
        }}
      >
        {calendarDays.map((day, i) => {
          if (!day) {
            return (
              <Box
                key={i}
                sx={{
                  height: "100%",
                  bgcolor: "#f8fafc",
                  borderRadius: "12px",
                  border: "1px dashed",
                  borderColor: alpha("#0f172a", 0.06),
                }}
              />
            );
          }

          const dayEvents = getDayEvents(day);
          const uniqueItems = uniqueDayItems(dayEvents);
          const rowCounts = showBranches
            ? getBranchCounts(uniqueItems)
            : getTypeCounts(uniqueItems);
          const total = uniqueItems.length;
          const ghostCount = uniqueItems.filter(isGhostOccurrence).length;
          const ghostOnly = total > 0 && ghostCount === total;
          const completed = uniqueItems.filter(
            (item) => item.status === "COMPLETED" && !isGhostOccurrence(item)
          ).length;
          const dayColor = getDayColor(dayEvents, ghostOnly);
          const bgColor = getDayBgColor(day, dayColor);
          const isDayToday = isToday(day, currentDate);
          const isSelected = selectedDay === day;

          return (
            <Card
              key={i}
              onClick={() => onDaySelect(day)}
              sx={{
                height: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                borderRadius: "12px",
                bgcolor: bgColor,
                border: ghostOnly || ghostTheme
                  ? `1.5px dashed ${isSelected ? dayColor : alpha(dayColor, 0.7)}`
                  : `1.5px solid ${isSelected ? dayColor : alpha(dayColor, 0.55)}`,
                cursor: "pointer",
                opacity: ghostOnly ? 0.78 : 1,
                boxShadow: isSelected
                  ? `0 8px 18px ${alpha(dayColor, 0.22)}`
                  : "0 1px 3px rgba(15,23,42,0.05)",
                transition:
                  "transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.22s ease, border-color 0.22s ease, background-color 0.22s ease",
                position: "relative",
                overflow: "hidden",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 10px 20px ${alpha(dayColor, 0.18)}`,
                  borderColor: dayColor,
                },
                "&::before": isDayToday
                  ? {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      bgcolor: isSelected ? dayColor : "#2563eb",
                    }
                  : {},
              }}
            >
              <CardContent
                sx={{
                  p: 0.9,
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  "&:last-child": { pb: 0.9 },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    color: dayColor,
                    fontSize: "0.92rem",
                    lineHeight: 1.1,
                    flexShrink: 0,
                  }}
                >
                  {day}
                </Typography>

                {total > 0 ? (
                  <>
                    <Box
                      sx={{
                        mt: 0.4,
                        mb: 0.55,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.58rem",
                          fontWeight: 700,
                          color: alpha("#0f172a", 0.55),
                          letterSpacing: "0.02em",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        Total {total}
                      </Typography>
                        {ghostCount > 0 ? (
                          <Typography
                            sx={{
                              fontSize: "0.58rem",
                              fontWeight: 800,
                              color: "#64748b",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {ghostCount} proy
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "0.58rem",
                              fontWeight: 800,
                              color:
                                completed === total
                                  ? "#15803d"
                                  : completed > 0
                                  ? "#c2410c"
                                  : alpha("#0f172a", 0.4),
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {completed} ok
                          </Typography>
                        )}
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.3,
                        overflow: "hidden",
                      }}
                    >
                      {rowCounts.map((item) => (
                        <Box
                          key={item.key}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.5,
                            px: showBranches ? 0.55 : 0.45,
                            py: showBranches ? 0.22 : 0.2,
                            borderRadius: "6px",
                            bgcolor: alpha(item.color, 0.1),
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            noWrap
                            sx={{
                              fontSize: showBranches ? "0.68rem" : "0.6rem",
                              fontWeight: 700,
                              color: item.color,
                              lineHeight: 1.2,
                              minWidth: 0,
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.62rem",
                              fontWeight: 800,
                              color: item.color,
                              lineHeight: 1,
                              fontVariantNumeric: "tabular-nums",
                              flexShrink: 0,
                            }}
                          >
                            {item.count}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

